
import React, { useState, useEffect, useCallback } from 'react';
import { useHistory } from "react-router-dom";
import useCancellationToken from 'utils/customHook/useCancellationToken';
import { customAxiosGetFunction } from 'utils/axiosFunction';
import * as reportCss from './DetailReport.css.js';
import { nvl } from 'utils/nvl';

// 카운트 함수
const countOperationValue = (auths, moduleCnt, etcValues) => {
  return (
    auths.map(auth => {
      if (auth.level === 2) {
        return nvl(moduleCnt[auth.authIdx], 0)
      }
      else {
        return 0
      }
    }).reduce((a, b) => a + b, 0) + etcValues
  )
}

const ReportHolding = ({ auths, beginDate, endDate, setReportMap }) => {
  const history = useHistory();
  const cancellationToken = useCancellationToken();
  const [moduleCnt, setModuleCnt] = useState(null);
  const [etcValuesByType, setEtcValuesByType] = useState({
    temp: 0,
    hf: 0,
    ph: 0,
    leakage: 0,
    etc: 0
  })
  const [etcValuesByAuth, setEtcValuesByAuth] = useState(
    auths.reduce((map, obj) => { if (obj.level === 2) map[obj.authIdx] = 0; return map; }, { 'etc': 0 })
  )
  const [detail, setDetail] = useState({});

  // 장치 보유데이터 조회 함수
  const havingModuleCount = useCallback(async (beginDate, endDate) => {
    customAxiosGetFunction(history, '/api/module/havingCnt', { params: { 'beginDate': beginDate, 'endDate': endDate } })
      .then(response => {
        setModuleCnt(response);
      })
      .catch(function (error) {
        console.log(error);
      });
  }, [])

  // 구분별 기타값 세팅
  const handleChangeSetEtcByType = useCallback((key, value) => {
    setEtcValuesByType((etcValuesByType) => {
      return {
        ...etcValuesByType,
        [key]: Number(value)
      }
    })
  }, [etcValuesByType])

  // 팀별 기타값 세팅 - 구분결 기타도 하나의 팀으로 간주
  const handleChangeSetEtcByAuth = useCallback((key, value) => {
    setEtcValuesByAuth((etcValuesByType) => {
      return {
        ...etcValuesByType,
        [key]: Number(value)
      }
    })
  }, [etcValuesByAuth])

  const handleChangeDetail = useCallback((x, value) => {
    setDetail(detail => {
      return {
        ...detail,
        [x]: value
      }
    })
  }, [])

  // 장치 보유데이터 조회
  useEffect(() => {
    if (cancellationToken.isCancelled) {
      return
    }

    havingModuleCount(beginDate, endDate);
  }, [beginDate, endDate, cancellationToken])

  // 레포트맵 세팅
  useEffect(() => {
    if (moduleCnt) {
      setReportMap((reportMap) => {
        return {
          ...reportMap,
          'holding': auths.reduce((map, obj) => {
            if (obj.level === 2) {
              map[obj.authNm] = {
                'gas': nvl(moduleCnt.gas[obj.authIdx], 0),
                'hf': nvl(moduleCnt.hf[obj.authIdx], 0),
                'ph': nvl(moduleCnt.ph[obj.authIdx], 0),
                'leakage': nvl(moduleCnt.leakage[obj.authIdx], 0),
                'etc': etcValuesByAuth[obj.authIdx],
              };
            }

            return map;
          }, {
            'etc': {
              ...etcValuesByType,
              'etc': etcValuesByAuth.etc,
            },
            'sum': {
              'gas': countOperationValue(auths, moduleCnt.gas, etcValuesByType.gas),
              'hf': countOperationValue(auths, moduleCnt.hf, etcValuesByType.hf),
              'ph': countOperationValue(auths, moduleCnt.ph, etcValuesByType.ph),
              'leakage': countOperationValue(auths, moduleCnt.leakage, etcValuesByType.leakage),
              'etc': countOperationValue(auths, etcValuesByAuth, etcValuesByAuth.etc),
            }
          }),
          'detail': {
            ...reportMap.detail,
            'holding': Object.keys(detail).map(x => { return detail[x] })
          }
        }
      })
    }
  }, [moduleCnt, etcValuesByType, etcValuesByAuth, detail])

  useEffect(() => () => cancellationToken.cancel(), []);

  if (!auths || !moduleCnt) {
    return null;
  }

  return (
    <>
      <reportCss.HoldingTitleBar>
        <span>2. 보유현황</span>
      </reportCss.HoldingTitleBar>
      <reportCss.HoldingContent>
        <div className="mainContent">
          <table>
            <thead>
              <tr>
                <th>구분</th>
                <th>휴대형<br />복합가스감지기</th>
                <th>휴대형<br />HF감지기</th>
                <th>우수로<br />설치댓수</th>
                <th>H<sub>2</sub>SO<sub>4</sub> Tank<br />설치댓수</th>
                <th>기타</th>
              </tr>
            </thead>
            <tbody>
              {
                auths.map(auth => {
                  if (auth.level === 2) {
                    return (
                      <tr key={auth.authIdx}>
                        <td>{auth.authNm}</td>
                        <td>{nvl(moduleCnt.gas[auth.authIdx], 0)}</td>
                        <td>{nvl(moduleCnt.hf[auth.authIdx], 0)}</td>
                        <td>{nvl(moduleCnt.ph[auth.authIdx], 0)}</td>
                        <td>{nvl(moduleCnt.leakage[auth.authIdx], 0)}</td>
                        <td>
                          <input type="text"
                            className="inputNumber"
                            defaultValue={etcValuesByAuth[auth.authIdx]}
                            onChange={({ target }) => { handleChangeSetEtcByAuth(auth.authIdx, target.value) }} />
                        </td>
                      </tr>
                    )
                  }
                })
              }
              <tr>
                <td>기타</td>
                <td>
                  <input type="text"
                    className="inputNumber"
                    defaultValue={etcValuesByType.gas}
                    onChange={({ target }) => { handleChangeSetEtcByType('gas', target.value) }} />
                </td>
                <td>
                  <input type="text"
                    className="inputNumber"
                    defaultValue={etcValuesByType.hf}
                    onChange={({ target }) => { handleChangeSetEtcByType('hf', target.value) }} />
                </td>
                <td>
                  <input type="text"
                    className="inputNumber"
                    defaultValue={etcValuesByType.ph}
                    onChange={({ target }) => { handleChangeSetEtcByType('ph', target.value) }} />
                </td>
                <td>
                  <input type="text"
                    className="inputNumber"
                    defaultValue={etcValuesByType.leakage}
                    onChange={({ target }) => { handleChangeSetEtcByType('leakage', target.value) }} />
                </td>
                <td>
                  <input type="text"
                    className="inputNumber"
                    defaultValue={etcValuesByAuth.etc}
                    onChange={({ target }) => { handleChangeSetEtcByAuth('etc', target.value) }} />
                </td>
              </tr>
              <tr>
                <td>계</td>
                <td>{countOperationValue(auths, moduleCnt.gas, etcValuesByType.gas)}</td>
                <td>{countOperationValue(auths, moduleCnt.hf, etcValuesByType.hf)}</td>
                <td>{countOperationValue(auths, moduleCnt.ph, etcValuesByType.ph)}</td>
                <td>{countOperationValue(auths, moduleCnt.leakage, etcValuesByType.leakage)}</td>
                <td>{countOperationValue(auths, etcValuesByAuth, etcValuesByAuth.etc)}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="subContent">
          <table>
            <thead>
              <tr>
                <th>보유 특기사항</th>
              </tr>
            </thead>
            <tbody>
              {
                [...Array(auths.length + 2).keys()].map((x) => {
                  return (
                    <tr key={`detail_${x}`}>
                      <td><input type="text"
                        className="inputText"
                        onChange={({ target }) => { handleChangeDetail(x, target.value) }} /></td>
                    </tr>
                  )
                })
              }
            </tbody>
          </table>
        </div>
      </reportCss.HoldingContent>
    </>
  )
}

export default React.memo(ReportHolding);